/**
 * Set-Konfigurator fuer den Mengenrabatt.
 *
 * Faengt den Kauf ab Menge 2 ab und fragt, wie sich die Menge auf die
 * Stimmungen verteilen soll. Wer vier Koshi kauft, will selten viermal
 * dieselbe Farbe, und ohne diese Frage merkt er es erst im Warenkorb.
 *
 * Der Rabatt haengt NICHT an diesem Dialog. Die Function rechnet ihn aus dem
 * Produkt-Metafeld, egal ob die Stuecke hier verteilt oder einzeln in den
 * Warenkorb gelegt wurden. Der Konfigurator verkauft den Rabatt, er berechtigt
 * nicht dazu. Deshalb setzt er auch keine Line-Item-Property, anders als der
 * Bundle-Button, dessen _bundle-Eigenschaft Voraussetzung fuer seine 5 % ist.
 *
 * Die Ersparnis hier ist eine Vorschau. Gerechnet wird clientseitig,
 * rabattiert ausschliesslich in der Function. Weichen beide ab, ist das ein
 * Fehler in dieser Datei, nicht im Preis.
 */
class VolumeSetBuilder extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;

    this.config = this.readConfig();
    if (!this.config) return;

    // Die Varianten stehen als Liste im Markup, siehe volume-tiers.liquid.
    // Von dort werden sie gelesen und nicht aus der Konfiguration: so gibt es
    // nur eine Quelle, und die uebersetzbaren Texte bleiben dort, wo Weglot
    // sie findet.
    this.items = Array.from(this.querySelectorAll("[data-variant-id]"));
    this.variants = this.items.map((item) => ({
      id: item.dataset.variantId,
      price: Number(item.dataset.price) || 0,
      available: Boolean(item.querySelector("[data-set-count]")),
    }));
    if (!this.variants.length) return;

    this.tiers = (this.config.tiers || [])
      .map((tier) => ({
        minQuantity: Number(tier.min_quantity),
        percentage: Number(tier.percentage),
      }))
      .filter((tier) => tier.minQuantity > 0 && tier.percentage > 0)
      .sort((a, b) => a.minQuantity - b.minQuantity);

    this.errorOutput = this.querySelector("[data-volume-set-error]");
    this.submitButton = this.querySelector("[data-volume-set-submit]");

    this.counts = new Map(this.variants.map((v) => [String(v.id), 0]));

    // Ankerpunkte des Anzeigers. Auf der Produktseite misst volume-tiers.js
    // die Mitten der Stufenkarten. Hier gibt es keine Karten, also werden
    // dieselben Bruchteile gerechnet, die dieses Raster ergaebe:
    // (Spaltenindex + 0,5) geteilt durch Spaltenzahl. Sichtbar ist der
    // Unterschied nicht, die Rasterabstaende machen wenige Pixel aus.
    const spalten = this.tiers.length + (this.config.hasBaseStep ? 1 : 0);
    const versatz = this.config.hasBaseStep ? 1 : 0;
    this.anchors = [];
    if (this.config.hasBaseStep) {
      this.anchors.push({ quantity: 1, percent: 50 / spalten, tier: null });
    }
    this.tiers.forEach((tier, i) => {
      this.anchors.push({
        quantity: tier.minQuantity,
        percent: ((i + versatz) * 2 + 1) * 50 / spalten,
        tier,
      });
    });

    this.bindList();
    this.renderProgress();
    this.submitButton.addEventListener("click", () => this.submit());
    this.ready = true;
    this.update();
  }

  readConfig() {
    const node = this.querySelector("[data-volume-set-config]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // Ohne belastbare Daten oeffnet dieser Dialog nicht. volume-tiers.js
      // prueft isReady und laesst den Kauf dann normal durchlaufen, statt den
      // Kunden vor einem leeren Fenster stehen zu lassen.
      console.error("[volume-set-builder] Konfiguration nicht lesbar", error);
      return null;
    }
  }

  get isReady() {
    return Boolean(this.ready);
  }

  /**
   * Verdrahtet die im Markup vorhandene Liste. Frueher baute diese Methode sie
   * selbst zusammen, samt "Ausverkauft" und den Vorlesetexten als deutsche
   * Zeichenketten im JavaScript. Auf der englischen Fassung waren die dadurch
   * nicht zu uebersetzen.
   */
  bindList() {
    this.items.forEach((item) => {
      const id = item.dataset.variantId;
      const feld = item.querySelector("[data-set-count]");
      if (!feld) return;

      item.querySelector("[data-set-minus]").addEventListener("click", () => this.step(id, -1));
      item.querySelector("[data-set-plus]").addEventListener("click", () => this.step(id, 1));
      feld.addEventListener("change", () => this.setCount(id, feld.value));
      feld.addEventListener("input", () => this.setCount(id, feld.value));
    });
  }

  /**
   * Setzt den Ausgangszustand: die gewaehlte Menge liegt zunaechst vollstaendig
   * auf der Stimmung, die der Kunde auf der Produktseite ausgewaehlt hatte.
   * Er verteilt von dort aus um. Ein leerer Dialog waere mehr Arbeit fuer
   * denselben Warenkorb.
   */
  prepare(quantity, variantId) {
    if (!this.isReady) return;
    const key = String(variantId);
    const treffer = this.counts.has(key) ? key : String(this.variants[0].id);
    this.counts.forEach((_, k) => this.counts.set(k, 0));
    this.counts.set(treffer, Math.max(1, Number(quantity) || 1));
    this.setError("");
    this.syncFields();
    this.update();
  }

  step(variantId, delta) {
    const key = String(variantId);
    this.counts.set(key, Math.max(0, (this.counts.get(key) || 0) + delta));
    this.syncFields();
    this.update();
  }

  setCount(variantId, value) {
    const zahl = parseInt(value, 10);
    this.counts.set(String(variantId), Number.isFinite(zahl) && zahl > 0 ? zahl : 0);
    this.update();
  }

  syncFields() {
    this.querySelectorAll("[data-set-count]").forEach((feld) => {
      const key = feld.closest("[data-variant-id]").dataset.variantId;
      feld.value = String(this.counts.get(key) || 0);
    });
  }

  get total() {
    let summe = 0;
    this.counts.forEach((n) => (summe += n));
    return summe;
  }

  get subtotal() {
    let summe = 0;
    this.variants.forEach((v) => {
      summe += (this.counts.get(String(v.id)) || 0) * v.price;
    });
    return summe;
  }

  tierFor(quantity) {
    let treffer = null;
    this.tiers.forEach((tier) => {
      if (quantity >= tier.minQuantity) treffer = tier;
    });
    return treffer;
  }

  /**
   * Der Anzeiger steht bereits im Markup, siehe volume-tiers.liquid. Hier
   * werden nur die Knoten gemerkt. Frueher baute diese Methode das Markup
   * selbst, und Weglot uebersetzte die so erzeugten Beschriftungen
   * uneinheitlich ("4 items" neben "10 pieces").
   */
  renderProgress() {
    this.totalOutput = this.querySelector("[data-volume-set-total]");
    this.fill = this.querySelector("[data-volume-set-fill]");
  }

  /**
   * Fuellstand zur Menge, nach derselben Regel wie auf der Produktseite.
   *
   * Der Balken wandert innerhalb einer Stufe mit: von 4 auf 5 ruckt er ein
   * Stueck vor, und so weiter bis zur 10. Bis zum 31.08.2026 stand er auf
   * halber Strecke und ruehrte sich zwischen zwei Schwellen nicht, was die
   * Frage "wie weit fehlt mir noch" unbeantwortet liess.
   *
   * Der Anfang jeder Stufe liegt eine halbe Markenbreite HINTER ihrer Marke.
   * Dadurch liegt die gerade erreichte Marke vollstaendig auf dem Gruen und
   * schwebt nicht mit ihrer rechten Haelfte ueber dem grauen Teil. Beim
   * Ueberschreiten einer Schwelle springt der Balken deshalb um diese halbe
   * Breite vor, ein kleiner Ruck genau im richtigen Moment.
   *
   * Hoechste Stufe erreicht: voll. Es ist nichts mehr zu holen, und ein
   * Balken, der bei 90 Prozent stehenbliebe, behauptete das Gegenteil.
   */
  progressFor(quantity) {
    const first = this.anchors[0];
    const last = this.anchors[this.anchors.length - 1];
    if (quantity <= first.quantity) return first.percent;
    if (quantity >= last.quantity) return 100;

    const versatz = this.overshootPercent();
    for (let i = 0; i < this.anchors.length - 1; i += 1) {
      const from = this.anchors[i];
      const to = this.anchors[i + 1];
      if (quantity < from.quantity || quantity >= to.quantity) continue;

      const start = i === 0 ? from.percent : from.percent + versatz;
      const spanne = to.quantity - from.quantity;
      const anteil = spanne > 0 ? (quantity - from.quantity) / spanne : 0;
      return start + (to.percent - start) * anteil;
    }
    return 100;
  }

  overshootPercent() {
    const tick = this.querySelector("[data-volume-set-tick]");
    const bahn = this.querySelector(".volume-set__track");
    if (!tick || !bahn) return 0;
    const breite = bahn.getBoundingClientRect().width;
    if (!breite) return 0;
    return (tick.getBoundingClientRect().width / 2 / breite) * 100;
  }

  updateProgress(gesamt, tier, ersparnis) {
    if (!this.anchors.length) return;

    if (this.totalOutput) this.totalOutput.textContent = String(gesamt);
    if (this.fill) this.fill.style.width = `${this.progressFor(gesamt)}%`;

    this.querySelectorAll("[data-volume-set-mark]").forEach((mark) => {
      const menge = Number(mark.dataset.quantity);
      const gruen = mark.querySelector("[data-volume-set-saving]");
      const grau = mark.querySelector("[data-volume-set-scale]");
      const aktiv = Boolean(tier) && tier.minQuantity === menge && ersparnis > 0;
      const erreicht = gesamt >= menge;

      gruen.hidden = !aktiv;
      const gruenBetrag = gruen.querySelector("[data-volume-amount]");
      if (aktiv && gruenBetrag) gruenBetrag.textContent = this.money(ersparnis);

      // Was erreicht ist, braucht kein Angebot mehr. Was gilt, steht gruen
      // daneben. Uebrig bleibt, was noch aussteht.
      grau.hidden = erreicht;
      const grauBetrag = grau.querySelector("[data-volume-amount]");
      if (!erreicht && grauBetrag) {
        const stufe = this.tiers.find((t) => t.minQuantity === menge);
        const wert = stufe ? Math.round((menge * this.averagePrice * stufe.percentage) / 100) : 0;
        grauBetrag.textContent = this.money(wert);
      }
    });

    this.querySelectorAll("[data-volume-set-tick]").forEach((step) => {
      step.classList.toggle("is-reached", gesamt >= Number(step.dataset.quantity));
    });
  }

  /**
   * Fuer die noch nicht erreichten Stufen laesst sich kein genauer Betrag
   * nennen, weil noch offen ist, welche Stimmungen dazukommen. Der
   * Durchschnittspreis ist die ehrlichste Schaetzung: bei gleichteuren
   * Stimmungen, dem Normalfall hier, ist er exakt.
   */
  get averagePrice() {
    if (!this.variants.length) return 0;
    const summe = this.variants.reduce((s, v) => s + v.price, 0);
    return summe / this.variants.length;
  }

  update() {
    const gesamt = this.total;
    const tier = this.tierFor(gesamt);

    // Der Rabatt gilt fuer alle Stimmungen zusammen, deshalb rechnet die
    // Ersparnis auf der Zwischensumme und nicht je Position. Die Stimmungen
    // kosten unterschiedlich viel, eine Rechnung ueber die Stueckzahl waere
    // falsch, sobald jemand mischt.
    const ersparnis = tier ? Math.round((this.subtotal * tier.percentage) / 100) : 0;
    this.updateProgress(gesamt, tier, ersparnis);

    this.querySelectorAll("[data-variant-id]").forEach((item) => {
      item.classList.toggle("is-chosen", (this.counts.get(item.dataset.variantId) || 0) > 0);
    });
  }

  async submit() {
    if (this.total < 1) {
      this.setError(this.message("leer"));
      return;
    }

    const items = [];
    this.counts.forEach((n, key) => {
      if (n > 0) items.push({ id: Number(key), quantity: n });
    });

    this.setError("");
    this.submitButton.setAttribute("disabled", "true");
    this.submitButton.classList.add("btn--loading");

    // Die Section-Liste laesst Shopify den Warenkorb-Ausklapper gleich
    // mitrendern, denselben Weg geht products-bundle.js.
    const drawer = document.querySelector("cart-drawer");
    const sections =
      drawer && typeof drawer.getSectionsToRender === "function"
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];

    try {
      const antwort = await fetch(window.FoxThemeSettings.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/javascript" },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const daten = await antwort.json();

      // Shopify meldet Fehler mit gesetztem status, nicht ueber den HTTP-Code.
      if (daten.status) {
        this.setError(daten.description || daten.message || this.message("abgelehnt"));
        return;
      }

      this.close();

      if (drawer && typeof drawer.renderContents === "function") {
        drawer.renderContents(daten);
      } else {
        window.location.href = window.FoxThemeSettings.routes.cart;
        return;
      }

      if (window.FoxThemeEvents) window.FoxThemeEvents.emit("ON_ITEM_ADDED", daten);
    } catch (error) {
      console.error("[volume-set-builder] Warenkorb nicht erreichbar", error);
      this.setError(this.message("netz"));
    } finally {
      this.submitButton.removeAttribute("disabled");
      this.submitButton.classList.remove("btn--loading");
    }
  }

  get modal() {
    return document.getElementById(this.dataset.modalId);
  }

  open(opener) {
    const modal = this.modal;
    if (modal && typeof modal.show === "function") modal.show(opener);
  }

  close() {
    const modal = this.modal;
    if (modal && typeof modal.hide === "function") modal.hide();
  }

  /**
   * Holt eine Meldung aus dem Markup. Dort steht sie als Text und ist damit
   * uebersetzbar, anders als eine Zeichenkette in dieser Datei.
   */
  message(schluessel) {
    const knoten = this.querySelector(`[data-volume-set-message="${schluessel}"]`);
    return knoten ? knoten.textContent.trim() : "";
  }

  setError(text) {
    if (!this.errorOutput) return;
    this.errorOutput.textContent = text;
    this.errorOutput.hidden = !text;
  }

  money(cents) {
    const format = window.FoxThemeSettings && window.FoxThemeSettings.money_format;
    if (typeof window.formatMoney === "function") return window.formatMoney(cents, format);
    return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
  }
}

if (!customElements.get("volume-set-builder")) {
  customElements.define("volume-set-builder", VolumeSetBuilder);
}
